import React, { useEffect, useState } from 'react';
import { useDeps } from 'src/hooks/useDeps';
import { scheduleIdle } from 'src/components/Scripts/scheduleIdle';

import { environments } from '../../env';

import { getOrCreateSplitKey } from './splitKey';
import { SplitReadyContext } from './SplitReadyContext';

/**
 * Defer Split.io SDK until idle so it does not compete with hydration.
 * Key is persisted so re-init after idle does not reshuffle traffic.
 *
 * Only the context value changes when the SDK arrives. Wrapping the app in
 * SplitFactoryProvider at that point would remount the whole tree, so consumers
 * read the factory through `SplitScope` instead.
 */
export const Splitio: React.FC<React.PropsWithChildren> = ({ children }) => {
    const deps = useDeps();
    const [factory, setFactory] = useState<SplitIO.IBrowserSDK | null>(null);

    useEffect(() => {
        let created: SplitIO.IBrowserSDK | null = null;
        let cancelled = false;
        const cancelIdle = scheduleIdle(() => {
            const isProd = deps.env.RELEASE_STAGE === 'production';
            const authorizationKey: string | any = isProd
                ? environments['master'].SPLITIO_AUTHORIZAYION_KEY
                : environments['cypress'].SPLITIO_AUTHORIZAYION_KEY;

            import(/* webpackChunkName: "splitio" */ '@splitsoftware/splitio-react')
                .then(({ SplitFactory }) => {
                    if (cancelled) return;
                    created = SplitFactory({
                        core: {
                            authorizationKey,
                            key: getOrCreateSplitKey(),
                        },
                    });
                    setFactory(created);
                })
                .catch(() => {
                    /* keep control treatments via SplitReadyContext=null */
                });
        }, 3000);
        return () => {
            cancelled = true;
            cancelIdle();
            created?.destroy();
        };
    }, [deps.env.RELEASE_STAGE]);

    return <SplitReadyContext.Provider value={factory}>{children}</SplitReadyContext.Provider>;
};
